// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate, useParams } from '@tanstack/react-router';

// material ui icons
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// material ui
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

// api
import {
  useDeleteUtilityProviderCoefficientMutation,
  useGetUtilityProviderCoefficientsQuery,
  useSetUtilityProviderCoefficientMutation,
} from '@/api/energy-analysis/utitlityProviderSlice';

// relative
import UtilityProviderCoefficientsDatagrid from './UtilityProviderCoefficientsDatagrid';

const UtilityProviderDetailPage = ({ utilityType }) => {
  const { t } = useTranslation('portfolio-analysis');
  const navigate = useNavigate();
  const { providerId } = useParams({ strict: false });

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newCoefficient, setNewCoefficient] = useState({
    co2e_coefficient_per_unit: '',
    start_time: null,
    end_time: null,
  });

  const {
    data: providersData,
    isLoading,
    error,
  } = useGetUtilityProviderCoefficientsQuery({
    utility_type: utilityType,
    provider_id: providerId,
  });

  const [setUtilityProviderCoefficient, { isLoading: isSaving }] =
    useSetUtilityProviderCoefficientMutation();

  const [deleteUtilityProviderCoefficient] =
    useDeleteUtilityProviderCoefficientMutation();

  const provider = providersData;

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    setNewCoefficient({
      co2e_coefficient_per_unit: '',
      start_time: null,
      end_time: null,
    });
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewCoefficient({
      co2e_coefficient_per_unit: '',
      start_time: null,
      end_time: null,
    });
  };

  const handleAddCoefficient = async () => {
    if (
      !newCoefficient.co2e_coefficient_per_unit ||
      !newCoefficient.start_time
    ) {
      return;
    }
    try {
      const body = {
        provider_id: providerId,
        co2e_coefficient_per_unit: parseFloat(
          newCoefficient.co2e_coefficient_per_unit,
        ),
        start_time: newCoefficient.start_time.toISO(),
      };
      await setUtilityProviderCoefficient({
        utility_type: utilityType,
        body,
      }).unwrap();

      handleCloseAddDialog();
    } catch (error) {
      console.error('Failed to add coefficient:', error);
    }
  };

  const handleDeleteCoefficient = async (coefficient) => {
    if (!window.confirm(t('utilityProviderDetailPage.confirmDelete'))) {
      return;
    }

    try {
      await deleteUtilityProviderCoefficient({
        utility_type: utilityType,
        provider_id: providerId,
        coefficient_id: coefficient.coefficient_id,
      }).unwrap();
    } catch (error) {
      console.error('Failed to delete coefficient:', error);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !provider) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {t('utilityProviderDetailPage.errorLoading')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h4">
            {provider.provider_name || provider.provider_id}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('utilityProviderDetailPage.providerId')}: {provider.provider_id}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          {t('utilityProviderDetailPage.addCoefficient')}
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('utilityProviderDetailPage.coefficientsTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('utilityProviderDetailPage.coefficientsDescription')}
          </Typography>
          <UtilityProviderCoefficientsDatagrid
            coefficients={provider.coefficients || []}
            onDeleteCoefficient={handleDeleteCoefficient}
          />
        </CardContent>
      </Card>

      {/* Add Coefficient Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('utilityProviderDetailPage.addCoefficientDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label={t(
                'utilityProviderDetailPage.addCoefficientDialog.coefficient',
              )}
              type="number"
              fullWidth
              value={newCoefficient.co2e_coefficient_per_unit}
              onChange={(e) =>
                setNewCoefficient({
                  ...newCoefficient,
                  co2e_coefficient_per_unit: e.target.value,
                })
              }
              helperText={t(
                'utilityProviderDetailPage.addCoefficientDialog.coefficientHelper',
              )}
              required
              slotProps={{
                htmlInput: { step: 0.0001, min: 0 }
              }}
            />
            <DatePicker
              label={t(
                'utilityProviderDetailPage.addCoefficientDialog.startTime',
              )}
              value={newCoefficient.start_time}
              onChange={(newValue) =>
                setNewCoefficient({ ...newCoefficient, start_time: newValue })
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={isSaving}>
            {t('utilityProviderDetailPage.addCoefficientDialog.cancel')}
          </Button>
          <Button
            onClick={handleAddCoefficient}
            variant="contained"
            disabled={
              !newCoefficient.co2e_coefficient_per_unit ||
              !newCoefficient.start_time ||
              isSaving
            }
          >
            {isSaving ? (
              <CircularProgress size={24} />
            ) : (
              t('utilityProviderDetailPage.addCoefficientDialog.add')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UtilityProviderDetailPage;
